import { useState, useEffect } from 'react';
import { Puzzle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { api } from '../lib/axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { toast } from 'sonner';

interface AuthFormProps {
  onSuccess: () => void;
}

export function AuthForm({ onSuccess }: AuthFormProps) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [isFromExtension, setIsFromExtension] = useState(false);

  // Check if coming from extension
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('auth_callback') === 'extension') {
      setIsFromExtension(true);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleDemoLogin = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: 'demo@linkvault.app',
        password: 'demo1234',
      });

      if (error) {
        console.error('Demo login error:', error);
        throw error;
      }

      if (data.session) {
        console.log('Demo login successful');
        if (isFromExtension) {
          toast.success('로그인 완료! 확장 프로그램이 연결되었어요');
        } else {
          toast.success('데모 계정으로 로그인했어요');
        }
        onSuccess();
      }
    } catch (error: any) {
      console.error('Demo login error details:', error);
      toast.error('로그인에 문제가 생겼어요');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isSignUp) {
        // Sign up
        console.log('Starting signup process...', { email, hasPassword: !!password });
        const response = await api.post('/signup', {
          email,
          password,
          fullName: fullName || null,
        });

        console.log('Signup successful:', response.data);
        toast.success('가입했어요! 로그인해주세요');
        setIsSignUp(false);
        setPassword('');
      } else {
        // Sign in
        console.log('Starting signin process...', { email });
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          console.error('Supabase signin error:', error);
          throw error;
        }

        if (data.session) {
          console.log('Signin successful, session created');
          if (isFromExtension) {
            toast.success('로그인 완료! 확장 프로그램이 연결되었어요');
          } else {
            toast.success('로그인했어요');
          }
          onSuccess();
        }
      }
    } catch (error: any) {
      console.error('Auth error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        error: error,
      });

      const errorMessage = error.response?.data?.error || error.message || '문제가 생겼어요';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-secondary/40 to-background p-4">
      <Card className="w-full max-w-sm space-y-5 p-6 shadow-lg">
        {/* Extension connection banner */}
        {isFromExtension && (
          <div className="flex items-center gap-2 rounded-lg bg-primary/10 px-3 py-2 text-sm text-primary">
            <Puzzle className="h-4 w-4 flex-shrink-0" />
            <span>로그인하면 확장 프로그램이 자동으로 연결됩니다</span>
          </div>
        )}

        <div className="space-y-1.5 text-center">
          <div className="flex justify-center">
            <img src="/images/logo-text.png" alt="Markly" className="h-12 w-auto" />
          </div>
        </div>

        <form onSubmit={handleEmailAuth} className="space-y-3">
          {isSignUp && (
            <div className="space-y-1.5">
              <Label htmlFor="fullName">이름</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="홍길동"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="email">이메일</Label>
            <Input
              id="email"
              type="email"
              placeholder="email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">비밀번호</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <Button type="submit" className="mt-1 w-full" disabled={loading}>
            {loading ? '처리 중...' : isSignUp ? '회원가입' : '로그인'}
          </Button>
        </form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">또는</span>
          </div>
        </div>

        <Button
          variant="outline"
          className="w-full border-primary/20 hover:border-primary/30 hover:bg-primary/5"
          onClick={handleDemoLogin}
          type="button"
          disabled={loading}
        >
          데모 계정으로 체험하기
        </Button>

        <div className="text-center text-sm">
          <button
            type="button"
            onClick={() => setIsSignUp(!isSignUp)}
            className="font-medium text-primary transition-colors hover:text-primary/80"
          >
            {isSignUp ? '이미 계정이 있으신가요? 로그인' : '계정이 없으신가요? 회원가입'}
          </button>
        </div>
      </Card>
    </div>
  );
}
